/**
 * Price-target configuration view (Req 6.1, 6.4, 6.5, 6.6, 6.7, 6.8).
 *
 * Per crop, lets the user create/update/delete a breakeven target and a
 * desired-profit target. `GET /api/targets/{crop}` lists current targets;
 * `PUT /api/targets/{crop}/{type}` saves (a breakeven target takes a direct
 * price; a profit target takes a per-bushel profit amount stored as
 * breakeven + profit — Req 6.3); `DELETE /api/targets/{crop}/{type}` removes a
 * target. Validation errors are surfaced inline (Req 6.6, 6.7) and a delete of a
 * non-existent target surfaces the server's not-found message (Req 6.8).
 */
import type { ApiClient, TargetBody } from "../api/client";
import { RANGES, cropLabel, type Crop, type TargetType } from "../config";
import type { FieldError, PriceTarget } from "../api/types";
import { el, render } from "../ui/dom";
import { formatPrice2, formatUtc } from "../ui/format";

/** Build the targets view for one crop. */
export function targetsView(api: ApiClient, crop: Crop): HTMLElement {
  const breakevenPanel = el("div", { class: "card target-panel" });
  const profitPanel = el("div", { class: "card target-panel" });

  const root = el("section", { class: "view targets-view" }, [
    el("h2", { class: "view-title", text: `${cropLabel(crop)} · price targets` }),
    breakevenPanel,
    profitPanel,
  ]);

  void reload();

  async function reload(): Promise<void> {
    const result = await api.getTargets(crop);
    const byType = new Map<TargetType, PriceTarget>();
    if (result.ok) {
      for (const t of result.data.targets) {
        byType.set(t.type, t);
      }
    }
    renderPanel(breakevenPanel, "breakeven", byType.get("breakeven"));
    renderPanel(profitPanel, "profit", byType.get("profit"));
  }

  function renderPanel(
    panel: HTMLElement,
    type: TargetType,
    existing: PriceTarget | undefined
  ): void {
    const isProfit = type === "profit";
    const title = isProfit ? "Desired-profit target" : "Breakeven target";
    const fieldLabel = isProfit ? "Desired profit per bushel" : "Target price per bushel";

    const error = el("p", { class: "field-error", attrs: { role: "alert" } });
    const message = el("p", { class: "form-message", attrs: { "aria-live": "polite" } });

    const input = el("input", {
      type: "number",
      name: `${type}Value`,
      class: "field-input",
      attrs: {
        "aria-label": fieldLabel,
        inputmode: "decimal",
        step: "0.01",
        min: "0.01",
        max: String(RANGES.priceTarget.max),
      },
    }) as HTMLInputElement;

    const save = el("button", { type: "submit", class: "btn btn-primary", text: "Save" });
    const del = el("button", {
      type: "button",
      class: "btn btn-danger",
      text: "Delete",
      ...(existing === undefined ? { disabled: true } : {}),
    });

    let busy = false;

    const summary =
      existing !== undefined
        ? el("p", { class: "value-meta" }, [
            `Current target: ${formatPrice2(existing.targetPrice)}/bu`,
            existing.lastNotifiedAt !== undefined
              ? ` · last notified ${formatUtc(existing.lastNotifiedAt)}`
              : "",
          ])
        : el("p", { class: "muted", text: "No target set." });

    const form = el(
      "form",
      {
        class: "target-form",
        on: {
          submit: (event: Event) => {
            event.preventDefault();
            void submit();
          },
        },
      },
      [
        el("label", { class: "field-label", text: fieldLabel }),
        input,
        el("div", { class: "btn-row" }, [save, del]),
        error,
        message,
      ]
    );

    del.addEventListener("click", () => void remove());

    async function submit(): Promise<void> {
      if (busy) return;
      error.textContent = "";
      message.textContent = "";
      message.className = "form-message";

      const value = Number(input.value.trim());
      if (input.value.trim().length === 0 || !Number.isFinite(value)) {
        error.textContent = "Enter a numeric value.";
        return;
      }

      const body: TargetBody = isProfit ? { profitAmount: value } : { targetPrice: value };

      busy = true;
      save.setAttribute("disabled", "");
      message.textContent = "Saving…";

      const result = await api.putTarget(crop, type, body);

      busy = false;
      save.removeAttribute("disabled");

      if (result.ok) {
        message.className = "form-message is-info";
        message.textContent = "Target saved.";
        void reload();
        return;
      }

      const responseBody = result.body as { fieldErrors?: FieldError[] };
      if (result.status === 400 && Array.isArray(responseBody.fieldErrors)) {
        error.textContent = responseBody.fieldErrors.map((fe) => fe.message).join(" ");
        return;
      }
      message.className = "form-message is-error";
      message.textContent =
        result.status >= 500
          ? "The target was not saved. Please try again."
          : "Could not save the target.";
    }

    async function remove(): Promise<void> {
      if (busy) return;
      error.textContent = "";
      message.textContent = "";
      message.className = "form-message";

      busy = true;
      del.setAttribute("disabled", "");

      const result = await api.deleteTarget(crop, type);

      busy = false;
      del.removeAttribute("disabled");

      if (result.ok) {
        message.className = "form-message is-info";
        message.textContent = "Target deleted.";
        void reload();
        return;
      }
      if (result.status === 404) {
        const body = result.body as { message?: string };
        message.className = "form-message is-error";
        message.textContent = body.message ?? "No such target exists.";
        return;
      }
      message.className = "form-message is-error";
      message.textContent = "Could not delete the target.";
    }

    render(
      panel,
      el("h3", { class: "panel-title", text: title }),
      summary,
      isProfit
        ? el("p", {
            class: "muted",
            text: "Stored as your breakeven price plus this profit amount. Requires a stored breakeven.",
          })
        : el("span", {}),
      form
    );

    if (existing !== undefined) {
      input.value = isProfit && existing.profitAmount !== undefined
        ? String(existing.profitAmount)
        : String(existing.targetPrice);
    }
  }

  return root;
}
